import MarketingShell from "@/components/marketing/MarketingShell";
import SEO from "@/components/marketing/SEO";

export default function Privacy() {
  return (
    <MarketingShell>
      <SEO
        title="Privacy Policy — TAXINPHO"
        description="How TAXINPHO collects, uses and protects your data. Compliant with IT Act 2000, DPDP Act 2023. TLS 1.3 encryption, bcrypt passwords, Indian data hosting. Your data stays yours."
        canonical="https://taxinpho.co/privacy"
      />
      <article className="max-w-3xl mx-auto px-5 py-16 md:py-20">
        <p className="text-xs font-bold uppercase tracking-widest text-[#002FA7] mb-2">Legal</p>
        <h1 className="font-display font-black text-5xl tracking-tighter mb-3">Privacy Policy</h1>
        <p className="text-sm text-zinc-500 mb-8">Last updated: 29 June 2026</p>

        <Section title="1. Introduction">
          This Privacy Policy describes how <strong>TAXINPHO</strong> (a sole
          proprietorship of <strong>Nirmala Agrawal</strong>, Trade License No.
          TL/BLR/2023-07-14/033462, Balangir, Odisha) (&quot;we&quot;, &quot;us&quot;, &quot;TAXINPHO&quot;)
          collects, uses, stores, and shares your personal
          information when you use the TAXINPHO platform. This policy complies with the
          Information Technology Act, 2000, the IT (Reasonable Security Practices) Rules
          2011, and the Digital Personal Data Protection Act, 2023.
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>Account information:</strong> Name, email address, hashed password,
          firm details (firm name, PAN, GSTIN), and phone number provided at registration.</p>
          <p><strong>Customer data:</strong> Information you upload about your clients,
          including names, contact details, GSTINs, PANs, addresses, invoice data, return
          data, and uploaded documents. This data belongs to you.</p>
          <p><strong>Payment data:</strong> We do NOT store your card numbers or UPI
          credentials. Payments are handled by our PCI-DSS compliant payment gateway.
          We only retain the transaction ID and amount.</p>
          <p><strong>Usage data:</strong> IP address, browser type, device information,
          and pages visited, collected via standard server logs for security and analytics.</p>
        </Section>

        <Section title="3. How We Use Your Data">
          <ul className="list-disc list-inside space-y-1 ml-3">
            <li>To provide and operate the TAXINPHO service</li>
            <li>To authenticate you and prevent fraud</li>
            <li>To process subscription payments and send invoices</li>
            <li>To send service notifications (e.g., expiry reminders, security alerts)</li>
            <li>To respond to your support requests</li>
            <li>To improve the Service based on aggregate, anonymised usage patterns</li>
          </ul>
        </Section>

        <Section title="4. AI Processing">
          When you use AI features (HSN finder, WhatsApp draft, client insight, etc.),
          we send the minimum necessary context to our AI provider (OpenAI / Anthropic
          via the Emergent LLM gateway). We do NOT send your full client list,
          passwords, or sensitive financial data unless required by the specific
          feature. AI providers do not use your data for training when accessed through
          our enterprise API gateway.
        </Section>

        <Section title="5. Data Sharing">
          <p>We do NOT sell, rent, or trade your personal information. We share data only with:</p>
          <ul className="list-disc list-inside space-y-1 ml-3">
            <li><strong>Payment gateway</strong> (Razorpay / Cashfree / etc.) — only payment-related fields, for processing your subscription</li>
            <li><strong>Cloud hosting provider</strong> (currently Emergent Cloud, India region) — to store your data securely</li>
            <li><strong>AI provider</strong> (Emergent LLM gateway) — only when you use AI features, with the minimum context required</li>
            <li><strong>Law enforcement</strong> — only when legally compelled by a valid court order or government notice</li>
          </ul>
        </Section>

        <Section title="6. Data Security">
          <p>We use industry-standard security measures:</p>
          <ul className="list-disc list-inside space-y-1 ml-3">
            <li>TLS 1.3 encryption for all data in transit</li>
            <li>Bcrypt-hashed passwords (never stored in plaintext)</li>
            <li>Role-based access control (only authorised team members access your data)</li>
            <li>Encrypted database at rest</li>
            <li>Daily automated backups</li>
            <li>Server-side audit logs of all sensitive operations</li>
          </ul>
        </Section>

        <Section title="7. Data Retention">
          We retain your data for as long as your account is active. Upon account
          closure, data is held for 30 days for recovery, then permanently deleted from
          live systems within 90 days. Backups are purged within 180 days. Some
          transactional records (invoices, GST documents) may be retained longer if
          required by Indian tax law.
        </Section>

        <Section title="8. Your Rights">
          <p>Under Indian data-protection law, you have the right to:</p>
          <ul className="list-disc list-inside space-y-1 ml-3">
            <li>Access your personal data we hold</li>
            <li>Correct inaccurate information</li>
            <li>Export your data in a portable format (CSV/JSON)</li>
            <li>Request deletion (subject to legal retention requirements)</li>
            <li>Withdraw consent for non-essential processing</li>
          </ul>
          <p>To exercise any of these rights, email
          <a href="mailto:taxinpho@gmail.com" className="text-[#002FA7] underline mx-1">taxinpho@gmail.com</a>
          from your registered email address. We respond within 30 days.</p>
        </Section>

        <Section title="9. Cookies">
          We use only essential cookies for authentication (storing your session token).
          We do NOT use third-party advertising or tracking cookies.
        </Section>

        <Section title="10. Children">
          TAXINPHO is intended for users 18 years and older. We do not knowingly
          collect data from minors. If you believe a minor has provided us data, please
          email us and we will delete it.
        </Section>

        <Section title="11. International Users">
          Our servers are located in India. By using TAXINPHO, you consent to your
          data being processed in India under Indian law. Cross-border transfers occur
          only via our AI provider and only when you use AI features.
        </Section>

        <Section title="12. Changes to This Policy">
          We may update this Policy. Material changes will be notified via email and
          in-app banner. Continued use after the effective date constitutes acceptance.
        </Section>

        <Section title="13. Grievance Officer">
          In accordance with the IT Act 2000 and DPDP Act 2023, you may contact our
          Grievance Officer for any complaints:
          <br /><strong>Name:</strong> Adv Mrinal Agrawal (Managing)
          <br /><strong>Email:</strong> <a href="mailto:taxinpho@gmail.com" className="text-[#002FA7] underline">taxinpho@gmail.com</a>
          <br /><strong>Phone:</strong> +91 95564 72222 / +91 88950 06864
          <br /><strong>Address:</strong> TAXINPHO, Shree Krishna Kutir, Shree Ram Colony, Thikadar Para, Balangir, Odisha - 767001, India
          <br /><strong>Response time:</strong> within 30 days
        </Section>
      </article>
    </MarketingShell>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-display font-bold text-xl tracking-tight mb-3 text-zinc-900">{title}</h2>
      <div className="text-zinc-700 text-sm leading-relaxed space-y-2.5">{children}</div>
    </section>
  );
}
